import type { FormEvent, ReactElement } from 'react';
import { useState } from 'react';
import type { AdminCuisineTypes,CuisineTypePublic } from '@cookspace-shared-contracts/core';
import { Pencil, Plus, Trash2 } from 'lucide-react';

import {
    useCreateCuisineTypeMutation,
    useDeleteCuisineTypeMutation,
    useListCuisineTypesQuery,
    useReorderCuisineTypesMutation,
    useUpdateCuisineTypeMutation
} from '@/api/cuisineTypesApi';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { SortableList } from '@/components/SortableList';
import { Switch } from '@/components/Switch';

const SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

interface FormState {
    name: string;
    slug: string;
    sortOrder: string;
    isActive: boolean;
}

interface FormErrors {
    name?: string;
    slug?: string;
}

const EMPTY_FORM: FormState = {
    name: '',
    slug: '',
    sortOrder: '0',
    isActive: true
};

/** 409 от backend = конфликт уникальности (slug) или тип кухни используется. */
function isConflict(error: unknown): boolean {
    return (
        typeof error === 'object' &&
        error !== null &&
        'status' in error &&
        (error as { status?: unknown }).status === 409
    );
}

export function CuisineTypesPage(): ReactElement {
    const [filterActiveOnly, setFilterActiveOnly] = useState(false);

    const { data, isLoading, isError } = useListCuisineTypesQuery(
        filterActiveOnly ? { isActive: true } : {}
    );

    const [createCuisineType, { isLoading: isCreating }] = useCreateCuisineTypeMutation();
    const [updateCuisineType, { isLoading: isUpdating }] = useUpdateCuisineTypeMutation();
    const [deleteCuisineType] = useDeleteCuisineTypeMutation();
    const [reorderCuisineTypes] = useReorderCuisineTypesMutation();

    const [isModalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<CuisineTypePublic | null>(null);
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [formErrors, setFormErrors] = useState<FormErrors>({});
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [pageError, setPageError] = useState<string | null>(null);
    const [pendingDelete, setPendingDelete] = useState<CuisineTypePublic | null>(null);

    const items = data?.items ?? [];

    const openCreate = (): void => {
        setEditing(null);
        setForm({ ...EMPTY_FORM, sortOrder: String(items.length) });
        setFormErrors({});
        setSubmitError(null);
        setModalOpen(true);
    };

    const openEdit = (cuisineType: CuisineTypePublic): void => {
        setEditing(cuisineType);
        setForm({
            name: cuisineType.name,
            slug: cuisineType.slug,
            sortOrder: String(cuisineType.sortOrder),
            isActive: cuisineType.isActive
        });
        setFormErrors({});
        setSubmitError(null);
        setModalOpen(true);
    };

    const validate = (): boolean => {
        const errors: FormErrors = {};
        if (!form.name.trim()) {
            errors.name = 'Название обязательно';
        }
        if (!form.slug.trim()) {
            errors.slug = 'Slug обязателен';
        } else if (!SLUG_REGEX.test(form.slug)) {
            errors.slug = 'Только латиница, цифры и дефисы';
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        setSubmitError(null);
        if (!validate()) {
            return;
        }

        const sortOrderNum = Number(form.sortOrder);
        const sortOrder = Number.isFinite(sortOrderNum) && sortOrderNum >= 0 ? sortOrderNum : 0;

        try {
            if (editing) {
                const body: AdminCuisineTypes.UpdateCuisineTypeRequest = {
                    name: form.name.trim(),
                    slug: form.slug.trim(),
                    sortOrder,
                    isActive: form.isActive
                };
                await updateCuisineType({ id: editing.id, body }).unwrap();
            } else {
                const body: AdminCuisineTypes.CreateCuisineTypeRequest = {
                    name: form.name.trim(),
                    slug: form.slug.trim(),
                    sortOrder,
                    isActive: form.isActive
                };
                await createCuisineType(body).unwrap();
            }
            setModalOpen(false);
        } catch (error) {
            setSubmitError(
                isConflict(error)
                    ? 'Тип кухни с таким slug уже существует'
                    : 'Не удалось сохранить тип кухни'
            );
        }
    };

    const confirmDelete = async (): Promise<void> => {
        if (!pendingDelete) {
            return;
        }
        try {
            await deleteCuisineType({ id: pendingDelete.id }).unwrap();
            setPendingDelete(null);
        } catch (error) {
            setPendingDelete(null);
            setPageError(
                isConflict(error)
                    ? 'Тип кухни используется блюдами — удаление невозможно, деактивируйте его'
                    : 'Не удалось удалить тип кухни'
            );
        }
    };

    const handleToggleActive = async (
        cuisineType: CuisineTypePublic,
        next: boolean
    ): Promise<void> => {
        try {
            await updateCuisineType({ id: cuisineType.id, body: { isActive: next } }).unwrap();
        } catch {
            setPageError('Не удалось изменить статус');
        }
    };

    const handleReorder = async (orderedIds: string[]): Promise<void> => {
        try {
            await reorderCuisineTypes({ orderedIds }).unwrap();
        } catch {
            setPageError('Не удалось изменить порядок');
        }
    };

    return (
        <div data-testid="cuisine-types-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">Типы кухонь</h1>
                <button
                    type="button"
                    onClick={openCreate}
                    data-testid="cuisine-type-create-button"
                    className="inline-flex items-center gap-2 rounded bg-gray-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
                >
                    <Plus size={14} />
                    Создать тип кухни
                </button>
            </div>

            <div className="flex flex-wrap items-center gap-4 rounded border border-gray-200 bg-white p-3">
                <Switch
                    checked={filterActiveOnly}
                    onChange={setFilterActiveOnly}
                    label="Только активные"
                    testId="cuisine-type-filter-active"
                />
            </div>

            {pageError !== null && pageError !== '' && (
                <div
                    className="flex items-center justify-between rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                    data-testid="cuisine-types-page-error"
                >
                    <span>{pageError}</span>
                    <button
                        type="button"
                        onClick={() => setPageError(null)}
                        className="text-xs text-red-700 underline"
                    >
                        Закрыть
                    </button>
                </div>
            )}

            {isLoading && <div className="text-sm text-gray-500">Загрузка...</div>}
            {isError && (
                <div className="text-sm text-red-600" data-testid="cuisine-types-error">
                    Не удалось загрузить типы кухонь
                </div>
            )}

            {!isLoading && !isError && (
                <div
                    className="overflow-hidden rounded border border-gray-200 bg-white"
                    data-testid="cuisine-types-table"
                >
                    <div className="grid grid-cols-[40px_80px_minmax(0,1fr)_minmax(0,1fr)_80px_80px_140px] gap-2 border-b border-gray-200 bg-gray-50 px-2 py-2 text-xs font-medium uppercase text-gray-600">
                        <span />
                        <span>ID</span>
                        <span>Название</span>
                        <span>Slug</span>
                        <span>Порядок</span>
                        <span>Активна</span>
                        <span className="text-right">Действия</span>
                    </div>

                    {items.length === 0 && (
                        <div className="px-3 py-6 text-center text-sm text-gray-500">
                            Типы кухонь не найдены
                        </div>
                    )}

                    <SortableList
                        items={items}
                        onReorder={handleReorder}
                        testId="cuisine-types-sortable"
                        renderItem={(cuisineType) => (
                            <div
                                className="grid grid-cols-[80px_minmax(0,1fr)_minmax(0,1fr)_80px_80px_140px] items-center gap-2 border-b border-gray-100 px-2 py-2 text-sm last:border-b-0"
                                data-testid={`cuisine-type-row-${cuisineType.id}`}
                            >
                                <span className="truncate font-mono text-xs text-gray-500">
                                    {cuisineType.id.slice(0, 8)}
                                </span>
                                <span className="truncate text-gray-900">{cuisineType.name}</span>
                                <span className="truncate text-gray-600">{cuisineType.slug}</span>
                                <span className="text-gray-700">{cuisineType.sortOrder}</span>
                                <span>
                                    <Switch
                                        checked={cuisineType.isActive}
                                        onChange={(next) => handleToggleActive(cuisineType, next)}
                                        testId={`cuisine-type-active-${cuisineType.id}`}
                                    />
                                </span>
                                <span className="flex justify-end gap-1">
                                    <button
                                        type="button"
                                        onClick={() => openEdit(cuisineType)}
                                        data-testid={`cuisine-type-edit-${cuisineType.id}`}
                                        className="rounded p-1.5 text-gray-600 transition hover:bg-gray-100"
                                        aria-label="Редактировать"
                                    >
                                        <Pencil size={14} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPendingDelete(cuisineType)}
                                        data-testid={`cuisine-type-delete-${cuisineType.id}`}
                                        className="rounded p-1.5 text-red-600 transition hover:bg-red-50"
                                        aria-label="Удалить"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </span>
                            </div>
                        )}
                    />
                </div>
            )}

            <Modal
                isOpen={isModalOpen}
                onClose={() => setModalOpen(false)}
                title={editing ? 'Редактировать тип кухни' : 'Новый тип кухни'}
                testId="cuisine-type-modal"
            >
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="cuisine-type-form">
                    <Input
                        label="Название"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        error={formErrors.name}
                        data-testid="cuisine-type-form-name"
                        autoFocus
                    />
                    <Input
                        label="Slug"
                        value={form.slug}
                        onChange={(e) => setForm({ ...form, slug: e.target.value })}
                        placeholder="example-slug"
                        error={formErrors.slug}
                        data-testid="cuisine-type-form-slug"
                    />
                    <Input
                        label="Порядок сортировки"
                        type="number"
                        value={form.sortOrder}
                        onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                        data-testid="cuisine-type-form-sort-order"
                    />
                    <Switch
                        checked={form.isActive}
                        onChange={(next) => setForm({ ...form, isActive: next })}
                        label="Активна"
                        testId="cuisine-type-form-is-active"
                    />

                    {submitError !== null && submitError !== '' && (
                        <div className="text-sm text-red-600" data-testid="cuisine-type-form-error">
                            {submitError}
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <button
                            type="button"
                            onClick={() => setModalOpen(false)}
                            className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-100"
                        >
                            Отмена
                        </button>
                        <button
                            type="submit"
                            disabled={isCreating || isUpdating}
                            data-testid="cuisine-type-form-submit"
                            className="rounded bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800 disabled:opacity-50"
                        >
                            {editing ? 'Сохранить' : 'Создать'}
                        </button>
                    </div>
                </form>
            </Modal>

            <ConfirmDialog
                isOpen={pendingDelete !== null}
                title="Удалить тип кухни"
                message={
                    pendingDelete
                        ? `Удалить тип кухни «${pendingDelete.name}»? Действие необратимо.`
                        : ''
                }
                confirmLabel="Удалить"
                danger
                onConfirm={() => {
                    void confirmDelete();
                }}
                onCancel={() => setPendingDelete(null)}
            />
        </div>
    );
}
